import React, { useState } from "react";

import { Button, Grid, Tag } from "antd";
import { MinusOutlined, PlusOutlined } from "@ant-design/icons";

import { BreakpointsToSpan, ColumnWithBreakpoints, ResponsiveAntdTable, ResponsiveAntdTableProps } from "@/Components/ResponsiveAntdTable";
import { Conjunto } from "@/Components/Conjunto";

import { TypePerfil } from "@/Models/Pessoa/Perfil";

import { Ordenar } from "@/Utils/Ordenacao";

import TablePaginadaBasic from "@/Components/Base/Table/TablePaginadaBasic";
import { useRequerPermissoes } from "@/Hooks/useRequerPermissoes";

type RTableProps = React.ComponentProps<typeof ResponsiveAntdTable<TypePerfil>>;
type PTableProps = React.ComponentProps<typeof TablePaginadaBasic<TypePerfil>>;

export const PerfilTableBasic = <T extends true | false = false>(props: {
    paginada?: T,
    expandColumns?: RTableProps["columns"];
    hideActions?: boolean,
    actionsBreakpoints?: Partial<BreakpointsToSpan>,
    actions?: (perfil: TypePerfil) => React.ComponentProps<typeof Conjunto>["itens"];
    tableProps?: T extends true ? PTableProps : RTableProps;
}) => {

    const breakpoint = Grid.useBreakpoint();
    const permissoes = useRequerPermissoes()
    const tamanhoDaTela = Grid.useBreakpoint();

    const [chavesDasLinhasExpandidas, setChavesDasLinhasExpandidas] = useState<React.Key[]>([]);

    const componentProps: Omit<RTableProps, "dataSource"> = {
        csv: {
            enabled: true,
            filename: "perfis.csv",
        },
        bordered: true,
        locale: { emptyText: "Nenhum perfil encontrado." },
        rowKey: (k) => k.codigo!,
        addTitleOn: "lg",
        expandable: {
            showExpandColumn: false,
            expandedRowRender: (record) => <div></div>,
            rowExpandable: () => true,
            expandedRowKeys: chavesDasLinhasExpandidas,
        },

        fitDefined: true,
        minGeneratedSpanWidth: breakpoint.lg ? 2 : breakpoint.sm ? 12 : 24,

        columns:
            props.tableProps?.columns ??
            ([
                {
                    title: "Código",
                    dataIndex: "codigo",
                    sorter: (a, b) => Ordenar.Numero(a.codigo, b.codigo),
                    breakpoints: { lg: 1 },
                    key: "codigo",
                },
                {
                    title: "Ativo",
                    dataIndex: ["ativo"],
                    key: "ativo",
                    sorter: (a, b) => Ordenar.Booleano(a.ativo, b.ativo),
                    render: (v: boolean) => {
                        return <>{v ? "Sim" : "Não"}</>;
                    },
                },
                {
                    title: "Nome",
                    dataIndex: ["nome"],
                    sorter: (a, b) => Ordenar.String(a.nome, b.nome),
                    key: "nome",
                },
                ...(props.expandColumns ?? []),
                {
                    title: "Ações",
                    key: "acoes",
                    noResponsiveTitle: true,
                    breakpoints: props.actionsBreakpoints,
                    hidden: props.hideActions,
                    render: (_, item) => {
                        return (
                            <Conjunto
                                style={{
                                    justifyContent: tamanhoDaTela.sm ? "unset" : "center",
                                    display: "flex",
                                    flexWrap: "wrap",
                                }}
                                itens={[
                                    <Button
                                        type="dashed"
                                        size="small"
                                        icon={item.codigo && chavesDasLinhasExpandidas.includes(item.codigo) ? <MinusOutlined /> : <PlusOutlined />}
                                        onClick={() => {
                                            if (!item.codigo) return;

                                            if (chavesDasLinhasExpandidas.includes(item.codigo)) {
                                                setChavesDasLinhasExpandidas((prev) => prev.filter((v) => v != item.codigo));
                                            } else {
                                                setChavesDasLinhasExpandidas((prev) => [...prev, item.codigo!]);
                                            }
                                        }}
                                    ></Button>,
                                    ...(props.actions?.(item) ?? []),
                                ]}
                            />
                        );
                    },
                },
            ] as RTableProps extends ResponsiveAntdTableProps<infer T> ? ColumnWithBreakpoints<T>[] : never),
    };

    if (props.paginada === true) {
        return <TablePaginadaBasic {...componentProps} {...(props.tableProps as PTableProps)} />;
    } else {
        return <ResponsiveAntdTable {...componentProps} {...(props.tableProps as RTableProps)} />;
    }

};